import { useContext } from "react";
import PropTypes from "prop-types";
import { LuxeContext } from "@context/LuxeProvider";
import { useAuth } from "@context/AuthProvider";
import luxePerks from "@components/sideCart/luxeSelection/perks.json";

import { useCurrentFormatCurrency } from "@hooks/usePrices";
import useDiscountsInfo, { DISCOUNT_INFO_MODES } from "@hooks/useDiscountsInfo";
import { useSubscriptionPopup } from "@context/SubscriptionPopupProvider";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { cn } from "@helpers/general";

import LuxeMemberCell from "@components/sideCart/luxeSelection/LuxeMemberCell";
import GuestCell from "@components/sideCart/luxeSelection/GuestCell";

import DISCOUNT_TYPES from "@constants/DiscountTypes";

function PerksTable({ sideCartInfo }) {
  const { luxe, setLuxeActive } = useContext(LuxeContext);
  const luxeDiscountsInfo = useDiscountsInfo(DISCOUNT_INFO_MODES.LUXE_ONLY);
  const nonLuxeDiscountsInfo = useDiscountsInfo(DISCOUNT_INFO_MODES.NON_LUXE_ONLY);
  const { setSubscriptionPopupOpen } = useSubscriptionPopup();

  const { showLogin, accountDetails } = useAuth();
  const formatCurrency = useCurrentFormatCurrency();
  const isLuxe = luxe.isLuxeActive || luxe.hasLuxeInCart || luxe.shouldAddLuxeInNextATC;

  const tierDiscountNonLuxe = nonLuxeDiscountsInfo.find(
    ({ type }) => type === DISCOUNT_TYPES.AMOUNT_PERCENT
  );
  const maxTieredDiscountNonLuxe =
    tierDiscountNonLuxe?.thresholdResultPercentage?.[
      tierDiscountNonLuxe?.thresholdResultPercentage?.length - 1
    ] || 0;
  const tierDiscountLuxe = luxeDiscountsInfo.find(
    ({ type }) => type === DISCOUNT_TYPES.AMOUNT_PERCENT
  );
  const maxTieredDiscountLuxe =
    tierDiscountLuxe?.thresholdResultPercentage?.[
      tierDiscountLuxe?.thresholdResultPercentage?.length - 1
    ] || 0;
  if (!luxe.isLuxeEnabled) return null;

  return (
    <div className="mt-[20px] mb-[20px] mx-[15px]">
      <p className="typo-h3-bold desktop:typo-body-bold">Pick the Way You Shop</p>
      <div className="flex flex-row mt-[15px]">
        <div className="w-[43%]" />
        <div className="w-[31%] flex justify-center">
          <div className="typo-micro-bold-caps px-[7px] py-[3px] bg-light-grey text-black rounded-[4px] mb-[7px]">
            BEST SAVINGS
          </div>
        </div>
        <div className="w-[26%]" />
      </div>
      <div className="bg-white rounded border border-[black]">
        {/* Header */}
        <div className="flex flex-col mb-[15px] desktop:mb-[20px]">
          {/* Best Savings Row */}

          {/* Perks rows */}
          {luxePerks.perks.map((perk, i) => (
            <div
              className={cn(
                "flex flex-row border-b",
                perk.type === "header" || perk.type === "footer"
                  ? "border-black"
                  : "border-dark-grey"
              )}
              key={`perk_${perk.type}_${i}`}
            >
              {/* Left Column: Label */}
              <div className={cn("w-[43%] pl-[10px] py-[7px] typo-micro", i === 1 && "font-bold")}>
                {perk.segments?.map((segment) => (
                  <span
                    key={segment.text}
                    className={cn(
                      segment.bold && "font-bold",
                      perk.type === "footer" && "leading-[21px]"
                    )}
                  >
                    {segment.text}
                  </span>
                ))}
              </div>

              {/* Member Column */}
              <button
                type="button"
                className={cn(
                  "flex justify-center items-center w-[31%] px-0 cursor-pointer typo-micro",
                  isLuxe ? "bg-light-grey text-black" : "bg-white text-black",
                  perk.type === "footer" ? "font-bold py-[7px]" : "py-[7px]",
                  i < 2 && "font-bold"
                )}
                onClick={() => setLuxeActive(true, luxe.subscriptionPlan, sideCartInfo)}
              >
                <LuxeMemberCell
                  perk={perk}
                  isLuxe={isLuxe}
                  sideCartInfo={sideCartInfo}
                  formatCurrency={formatCurrency}
                  maxTieredDiscountLuxe={maxTieredDiscountLuxe}
                />
              </button>

              {/* Guest Column */}
              <button
                type="button"
                className={cn(
                  "flex justify-center items-center w-[26%] px-0 cursor-pointer typo-micro bold",
                  isLuxe ? "bg-white" : "bg-light-grey",
                  perk.type === "footer" ? "font-bold" : "py-[7px]",
                  { "rounded-tr-lg": i === 0 }
                )}
                onClick={() => setLuxeActive(false, luxe.subscriptionPlan, sideCartInfo)}
              >
                <GuestCell
                  perk={perk}
                  isLuxe={isLuxe}
                  sideCartInfo={sideCartInfo}
                  formatCurrency={formatCurrency}
                  maxTieredDiscountNonLuxe={maxTieredDiscountNonLuxe}
                />
              </button>
            </div>
          ))}
        </div>

        {/* Agreement text */}
        {isLuxe && (
          <p className="typo-micro px-[10px] pb-[10px] desktop:pb-[15px] desktop:px-[15px]">
            <b>
              By purchasing, you choose to become an AL Luxe member and agree to the&nbsp;
              <a href="/pages/luxe-program-terms/" target="_blank" className="underline">
                Luxe Program Terms
              </a>
              , including a <b>$49.99</b> monthly payment converted to store credit.
            </b>
            &nbsp;Use your credit as you wish, anytime. You will pay a <b>$3.95</b> fee for the
            first trial month.
          </p>
        )}

        {/* Learn more + Sign in */}
        <div
          className={cn(
            "typo-micro font-bold flex justify-between px-[10px] cursor-pointer desktop:px-[15px]",
            isLuxe ? "pb-[10px]" : "pb-[15px]"
          )}
        >
          <button
            type="button"
            className="underline cursor-pointer"
            onClick={() => setSubscriptionPopupOpen(true)}
          >
            Learn more about AL Luxe membership
          </button>
          {!accountDetails && (
            <button
              type="button"
              className="flex items-center underline cursor-pointer"
              onClick={showLogin}
            >
              Sign In
            </button>
          )}
        </div>

        {/* Opt-out */}
        {isLuxe && (
          <button
            type="button"
            className="block typo-micro cursor-pointer font-bold underline px-[10px] pb-[15px] desktop:px-[15px] desktop:pb-[20px]"
            onClick={() => setLuxeActive(false, luxe.subscriptionPlan, sideCartInfo)}
          >
            To opt out, select &quot;Guest&quot;
          </button>
        )}
      </div>
    </div>
  );
}

PerksTable.propTypes = {
  sideCartInfo: PropTypes.shape({}).isRequired,
};

export default withALErrorBoundary({
  name: "SideCart",
  priority: "P1",
})(PerksTable);
